import * as React from "react";
import Box from "../Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { useNavigate, useLocation, Link } from "react-router-dom";
import theme from "@theme";
import SC from "./style";
import styled from "@emotion/styled";
import InnoIcons from "InnoUI/innoicons";

// Styled components for modern design
const StyledListItem = styled(ListItem)`
  background: ${(props) =>
    props.isSelected
      ? "linear-gradient(135deg, #E36E00 0%, #FFAE00 100%)"
      : "rgba(255, 255, 255, 0.6)"} !important;
  backdrop-filter: blur(8px);
  border-radius: 8px !important;
  box-shadow: ${(props) =>
    props.isSelected
      ? "0 4px 15px rgba(227, 110, 0, 0.25)"
      : "0 1px 4px rgba(0, 0, 0, 0.04)"} !important;
  margin: 6px 8px !important;
  border: 1px solid
    ${(props) =>
      props.isSelected
        ? "rgba(255, 255, 255, 0.2)"
        : "rgba(227, 110, 0, 0.08)"} !important;
  transition: all 0.2s ease !important;

  /* Force the menu label color based on selection — applied from the
     outer item so it can't be overridden by MUI Typography internals */
  & .MuiListItemText-root,
  & .MuiListItemText-primary,
  & .MuiTypography-root {
    color: ${(props) => (props.isSelected ? "white" : "#374151")} !important;
  }

  &:hover {
    background: ${(props) =>
      props.isSelected
        ? "linear-gradient(135deg, #FFAE00 0%, #E36E00 100%)"
        : "rgba(255, 255, 255, 0.8)"} !important;
    transform: translateX(3px) !important;
    box-shadow: ${(props) =>
      props.isSelected
        ? "0 6px 20px rgba(227, 110, 0, 0.3)"
        : "0 2px 8px rgba(227, 110, 0, 0.1)"} !important;

    & .MuiListItemText-root,
    & .MuiListItemText-primary,
    & .MuiTypography-root {
      color: ${(props) =>
        props.isSelected ? "white" : "#E36E00"} !important;
    }
  }
`;

const StyledListItemButton = styled(ListItemButton)`
  height: 48px !important;
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
  padding: ${(props) => (props.open ? "0 16px" : "0 8px")} !important;
  justify-content: ${(props) =>
    props.open ? "flex-start" : "center"} !important;

  &:hover {
    background: transparent !important;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none !important;
  color: ${(props) => (props.isSelected ? "white" : "#374151")} !important;
  width: 100% !important;
  display: flex !important;
  align-items: center !important;
  gap: ${(props) => (props.open ? "12px" : "0")} !important;
  font-weight: 500 !important;
  transition: all 0.2s ease !important;
  justify-content: ${(props) =>
    props.open ? "flex-start" : "center"} !important;

  &:hover {
    color: ${(props) => (props.isSelected ? "white" : "#E36E00")} !important;
  }
`;

const StyledListItemText = styled(ListItemText)`
  font-size: 13px !important;
  font-weight: 500 !important;
  margin: 0 !important;
  color: ${(props) => (props.isSelected ? "white" : "#374151")} !important;

  & .MuiListItemText-primary {
    color: ${(props) => (props.isSelected ? "white" : "#374151")} !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    white-space: nowrap;
    overflow: visible;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  font-size: 22px;
  color: ${(props) => (props.isSelected ? "white" : "#E36E00")};
  transition: all 0.2s ease;
`;

const styles = {
  list: {
    sx: {
      paddingTop: "unset !important",
      paddingBottom: "unset !important",
      padding: "16px",
      background: "rgba(255, 255, 255, 0.3)",
      backdropFilter: "blur(5px)",
    },
  },
};

const CustomizedList = ({ items, open }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Current path
  const currentPath = location.pathname;

  // Helper function to dynamically get the base path if it matches specified patterns
  const getBasePath = (path) => {
    const basePathPatterns = [
      /^\/admin-settings\/projects/,
      /^\/admin-settings\/folder-template/,
    ];

    for (const pattern of basePathPatterns) {
      if (pattern.test(path)) {
        return path.match(pattern)[0]; // Return the matched base path
      }
    }
    return path; // Return original path if no match
  };

  const handleItemClick = (route) => {
    const basePath = getBasePath(currentPath); // Adjust the current path
    navigate(route === basePath ? basePath : route); // Navigate accordingly
  };

  const pathname = getBasePath(currentPath);

  // Helper function to get icon for route
  const getIconForRoute = (route) => {
    if (route.includes("/admin-settings/projects")) {
      return <InnoIcons.FolderIcon />;
    } else if (route.includes("/admin-settings/folder-template")) {
      return <InnoIcons.CreateNewFolderOutlinedIcon />;
    } else if (route.includes("/admin-settings/users")) {
      return <InnoIcons.PersonRoundedIcon />;
    } else if (route.includes("/admin-settings/companies")) {
      return <InnoIcons.AccountBalanceIcon />;
    } else if (route.includes("/admin-settings/roles")) {
      return <InnoIcons.LockIcon />;
    } else if (route.includes("/admin-settings/holidays")) {
      return <InnoIcons.CalendarMonthIcon />;
    } else if (route.includes("/admin-settings/levels")) {
      return <InnoIcons.ListIcon />;
    } else if (route.includes("/admin-settings/reports")) {
      return <InnoIcons.TableChartIcon />;
    } else if (route.includes("/admin-settings/cart")) {
      return <InnoIcons.ShoppingCartIcon />;
    } else if (route.includes("/admin-settings/address-book")) {
      return <InnoIcons.AddressBook />;
    } else {
      return <InnoIcons.DashboardIcon />;
    }
  };

  return (
    <Box>
      {SC.GlobalStyles}
      <List sx={styles.list.sx}>
        {items.map((item, index) => {
          const isSelected = pathname === item.route;

          return (
            <StyledListItem key={index} disablePadding isSelected={isSelected}>
              <StyledListItemButton
                selected={isSelected}
                onClick={() => handleItemClick(item.route)}
                open={open}
              >
                <StyledLink to={item.route} isSelected={isSelected} open={open}>
                  <IconContainer isSelected={isSelected}>
                    {item.icon ? item.icon : getIconForRoute(item.route)}
                  </IconContainer>
                  {open && (
                    <StyledListItemText
                      primary={item.text}
                      isSelected={isSelected}
                    />
                  )}
                </StyledLink>
              </StyledListItemButton>
            </StyledListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default CustomizedList;
